import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import AccountIcon, {
  AccountIconProps,
} from 'shared/components/vxl/AccountIcon/AccountIcon';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import { Tag, TagProps } from 'shared/components/vxl/Tag/Tag';
import ExpressiveXSText from 'shared/components/vxl/typography/expressive/ExpressiveXSText';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

export interface AccountCardSquareProps {
  /** Circular icon to be displayed on the card*/
  accountIconProps: AccountIconProps;
  /** Boolean prop used to decide whether to show the plus icon or not*/
  showPlusIcon?: Maybe<boolean>;
  /** Title to be displayed on the card */
  titleText: string;
  /** Body to be displayed on the card. If you use bodyText prop, don't use amountText prop. If you use both, amountText will be the only one shown. */
  bodyText?: Maybe<string>;
  /** Amount to be displayed on the card. If you use amountText prop, don't use bodyText prop. If you use both, amountText will be the only one shown. */
  amountText?: Maybe<string>;
  /**  This prop accepts the Tag component props. If you use tagProps, don't use captionText. If you use both, tagProps will be the only one shown. */
  tagProps?: Maybe<TagProps>;
  /** Used to display the caption at the bottom of card. If you use captionText, don't use tagProps. If you use both, tagProps will be the only one shown. */
  captionText?: Maybe<string>;
  /** Use this if you need to set the number of lines for the caption text. A value of 0 will result in the number of lines being unrestricted. This will only have an effect if used in conjuction with captionText. */
  captionTextNumberOfLines?: number;
  /** This prop is used to disable the card interaction */
  isDisabled?: Maybe<boolean>;
  /** Function used to handle the card press action */
  onCardPress: () => void;
}

interface BodyOrAmountTextProps {
  bodyText?: AccountCardSquareProps['bodyText'];
  amountText?: AccountCardSquareProps['amountText'];
  isDisabled?: AccountCardSquareProps['isDisabled'];
}

interface TagOrCaptionProps {
  tagProps?: AccountCardSquareProps['tagProps'];
  captionText?: AccountCardSquareProps['captionText'];
  isDisabled?: AccountCardSquareProps['isDisabled'];
  captionTextNumberOfLines?: AccountCardSquareProps['captionTextNumberOfLines'];
}

const bodyTextLineLimit = 2;
const amountTextLineLimit = 1;
const titleTextLineLimit = 1;

const BodyOrAmountText = ({
  bodyText,
  amountText,
  isDisabled,
}: BodyOrAmountTextProps) => {
  if (amountText) {
    return (
      <ExpressiveXSText
        style={isDisabled && styles.disabledText}
        numberOfLines={amountTextLineLimit}
        ellipsizeMode="tail"
      >
        {amountText}
      </ExpressiveXSText>
    );
  }
  if (bodyText) {
    return (
      <BodyBaseBoldText
        style={isDisabled && styles.disabledText}
        numberOfLines={bodyTextLineLimit}
        ellipsizeMode="tail"
      >
        {bodyText}
      </BodyBaseBoldText>
    );
  }
  return null;
};

const TagOrCaption = ({
  tagProps,
  captionText,
  isDisabled,
  captionTextNumberOfLines,
}: TagOrCaptionProps) => {
  if (tagProps) {
    return <Tag {...tagProps} />;
  }
  if (captionText) {
    return (
      <BodyXSText
        style={[styles.caption, isDisabled && styles.disabledText]}
        numberOfLines={captionTextNumberOfLines}
        ellipsizeMode="tail"
      >
        {captionText}
      </BodyXSText>
    );
  }
  return null;
};

export const AccountCardSquare = ({
  accountIconProps,
  showPlusIcon,
  titleText,
  bodyText,
  amountText,
  tagProps,
  captionText,
  captionTextNumberOfLines = 2,
  isDisabled = false,
  onCardPress,
}: AccountCardSquareProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const iconColor = isDisabled
    ? theme.color.text.muted
    : theme.color.interactive.primary;

  return (
    <Pressable
      onPress={onCardPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      disabled={isDisabled ?? false}
      aria-disabled={isDisabled ?? false}
      role="button"
      accessible
      style={[
        styles.container,
        isFocused && styles.focus,
        isHovered && styles.hover,
        isPressed && styles.press,
      ]}
      testID={`${titleText} Tile`}
      accessibilityLabel={`${titleText} Tile`}
    >
      <View style={styles.topContainer}>
        <View style={styles.iconContainer}>
          <AccountIcon {...accountIconProps} />
          {showPlusIcon ? (
            <View style={styles.plusIconContainer} testID="plus-icon">
              <Icon iconKey="ADD" iconSize="SM" iconColor={iconColor} />
            </View>
          ) : null}
        </View>
        <BodySmText
          style={isDisabled && styles.disabledText}
          numberOfLines={titleTextLineLimit}
          ellipsizeMode="tail"
        >
          {titleText}
        </BodySmText>
        <BodyOrAmountText
          bodyText={bodyText}
          amountText={amountText}
          isDisabled={isDisabled}
        />
      </View>
      <TagOrCaption
        tagProps={tagProps}
        captionText={captionText}
        isDisabled={isDisabled}
        captionTextNumberOfLines={captionTextNumberOfLines}
      />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  caption: {
    color: theme.color.text.secondary,
  },
  container: {
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: 4,
    flex: 1,
    gap: theme.spacing.xs,
    justifyContent: 'space-between',
    minHeight: 172,
    padding: theme.spacing.m,
  },
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  plusIconContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.tertiary,
    borderRadius: 12,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  topContainer: {
    gap: theme.spacing.xs,
  },
});

export default AccountCardSquare;
